import { useMemo } from 'react';
import { ApolloError, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { ConsentType, UserFeature } from '__generated__/globalTypes';
import {
  GivenConsents,
  toGivenConsents,
} from 'LoggedOut/PrivacyConsent/consentRequirements';
import { useOutstandingConsents } from 'LoggedOut/PrivacyConsent/useOutstandingConsents';
import { UserNeedsAdditionalWorkDetails } from './__generated__/UserNeedsAdditionalWorkDetails';

export const USER_NEEDS_ADDITIONAL_INFORMATION = gql`
  query UserNeedsAdditionalWorkDetails {
    user {
      id
      location {
        value
      }
      department {
        value
      }
      group {
        locations {
          id
        }
        departments {
          id
        }
      }
      authenticationMethod
      explicitPrivacyConsentGiven {
        value
      }
      piplPrivacyPolicyConsentGiven
      piplSensitiveDataConsentGiven
      piplThirdPartySharingConsentGiven
      piplCrossBorderTransferConsentGiven
      features
    }
  }
`;

type UserNeedsAdditionalWorkDetailsReturn = {
  userNeedsAdditionalWorkDetails: boolean;
  userNeedsExplicitPrivacyConsent: boolean;
  /** No global privacy consent yet, so this is the first we ask them for any. */
  firstConsentCapture: boolean;
  outstandingConsents: ConsentType[];
  isSSOUser: boolean;
  loading: boolean;
  error: ApolloError | undefined;
};

const examineUserQueryResponse = (
  data: UserNeedsAdditionalWorkDetails | undefined,
  loading: boolean,
  error: ApolloError | undefined,
) => {
  let explicitPrivacyConsentNeeded = false;
  let userNeedsAdditionalWorkDetails = false;
  let isSSOUser = false;
  let givenConsents: GivenConsents | undefined;

  if (!loading && !error && data?.user) {
    const {
      user,
      user: {
        group,
        location: userLocation,
        department: userDepartment,
        authenticationMethod,
        explicitPrivacyConsentGiven,
        features,
      },
    } = data;

    givenConsents = toGivenConsents(user);
    isSSOUser = authenticationMethod === 'SSO';

    // Work details and the explicit privacy consent stay SSO-only: password users give
    // location, department and consent during registration.
    if (isSSOUser && group) {
      explicitPrivacyConsentNeeded =
        explicitPrivacyConsentGiven?.value !== 'true';

      const { locations, departments } = group;

      const groupHasLocations =
        (locations?.filter(l => l !== null)?.length ?? 0) > 0;
      const groupHasDepartments =
        (departments?.filter(d => d !== null)?.length ?? 0) > 0;

      const userHasLocation = Boolean(userLocation?.value ?? false);
      const userHasDepartment = Boolean(userDepartment?.value ?? false);

      userNeedsAdditionalWorkDetails =
        features.includes(UserFeature.DEPARTMENT_LOCATION_SELECTION) &&
        ((groupHasLocations && !userHasLocation) ||
          (groupHasDepartments && !userHasDepartment));
    }
  }

  return {
    explicitPrivacyConsentNeeded,
    userNeedsAdditionalWorkDetails,
    isSSOUser,
    givenConsents,
  };
};

export const useUserNeedsAdditionalWorkDetails =
  (): UserNeedsAdditionalWorkDetailsReturn => {
    const {
      data: userData,
      loading: userLoading,
      error: userError,
    } = useQuery<UserNeedsAdditionalWorkDetails>(
      USER_NEEDS_ADDITIONAL_INFORMATION,
      { fetchPolicy: 'cache-and-network' },
    );

    const {
      explicitPrivacyConsentNeeded,
      userNeedsAdditionalWorkDetails,
      isSSOUser,
      givenConsents,
    } = useMemo(
      () => examineUserQueryResponse(userData, userLoading, userError),
      [userData, userLoading, userError],
    );

    const { outstandingConsents, loading: requirementsLoading } =
      useOutstandingConsents(givenConsents);

    return {
      userNeedsAdditionalWorkDetails,
      userNeedsExplicitPrivacyConsent:
        explicitPrivacyConsentNeeded || outstandingConsents.length > 0,
      firstConsentCapture: explicitPrivacyConsentNeeded,
      outstandingConsents,
      isSSOUser,
      loading: userLoading || requirementsLoading,
      error: userError,
    };
  };
